import { makeAutoObservable } from 'mobx';
import _ from 'lodash';
import {successAlert, errorAlert} from 'shared/tools';
import {endpoints, types, auth} from 'main/tools';
import {t, locale} from 'shared/core';
import {v4 as uuidv4} from 'uuid';
import Document from 'main/stores/documents/Document';
import CompanyDocument from 'main/stores/documents/CompanyDocument';
import Folder from 'main/stores/folders/Folder';
import CompanyDocumentAssignment from 'main/stores/documents/CompanyDocumentAssignment';
import Employee from 'main/stores/employees/Employee';
import {DomainStore, fetchModels} from 'shared/store';

class EmployeeDocumentState {
  store = new DomainStore();

  editingDocument = {};
  editDocumentModalOpen = false;
  errors = {};
  presigner;
  employee;
  movingDocument = {};
  documentAction = '';
  companyDocumentAssignment;
  moveDocumentModalOpen = false;
  allFolders = [];
  companyDocuments;
  assignDocumentModalOpen = false;
  assigningDocument = {};
  acceptedMimeTypes;
  isUploading = false;
  annotatorModalOpen = false;
  interactiveAgent = null;
  uploadModalFolder = {};
  uploadModalShareWithEmployee = false;
  uploadModalFiles = [];
  uploadModalOpen = false;

  constructor(parentState) {
    makeAutoObservable(this, {
      store: false,
      interactiveAgent: false
    });

    this.parentState = parentState;
    this.presigner = endpoints.EMPLOYEE_DOCUMENTS.PRESIGNER.with(this.employeeId);
  }

  async load() {
    const composed = await this.store._compose([
      endpoints.FOLDERS.FOR_EMPLOYEE.ALL.with(this.employeeId)
    ]);

    if (auth.hasAccess('::MANAGE_DOCUMENTS')) {
      this.companyDocuments = await fetchModels(
        endpoints.COMPANY_DOCUMENTS.all,
        types.COMPANY_DOCUMENT
      );
    }

    this.employee = new Employee(this.store._getSingle(types.EMPLOYEE, {id: this.employeeId}));
    this.allFolders = composed[0].data.map(f => new Folder(f));
  }

  async handleUploadModalSave() {
    this.isUploading = true;
    try {
      const documentUploads = this.uploadModalFiles.map(async (file) => {
        await this.createDocument(file, this.uploadModalFolder, this.uploadModalShareWithEmployee);
      });

      await Promise.all(documentUploads);
    } finally {
      this.isUploading = false;
    }
    this.closeUploadModal();
  }

  setInteractiveAgent(agent) {
    this.interactiveAgent = agent;
  }

  async fetchFolderDocuments(folder) {
    const store = new DomainStore();
    const endpointsToLoad = [endpoints.EMPLOYEE_DOCUMENTS.withFilter(this.employeeId, folder.id)];

    if (folder.hasSubfolders) {
      endpointsToLoad.push(endpoints.FOLDERS.FOR_EMPLOYEE.with(this.employeeId, folder.id));
    }

    await store._compose(endpointsToLoad);
    folder.documents = store._getAll(types.DOCUMENT, Document);

    if (folder.hasSubfolders) {
      folder.subfolders = store._getAll(
        types.FOLDER,
        subfolder => subfolder.parentId?.toString() === folder.id,
        Folder
      );
    }
  }

  openUploadModal(folder) {
    this.uploadModalFolder = folder || {};
    this.uploadModalShareWithEmployee = false;
    this.uploadModalFiles = [];
    this.uploadModalOpen = true;
  }

  closeUploadModal() {
    this.uploadModalFolder = {};
    this.uploadModalShareWithEmployee = false;
    this.uploadModalFiles = [];
    this.uploadModalOpen = false;
  }

  openAssignDocumentModal(folder) {
    this.assigningDocument = new CompanyDocument({
      id: uuidv4(),
      locale: locale,
      folderId: _.get(folder, 'id'),
    });
    this.documentAction = '';
    this.companyDocumentAssignment = new CompanyDocumentAssignment({
      employees: [this.parentState.employee],
    });
    this.assignDocumentModalOpen = true;
  }

  openEditDocumentModal(model) {
    this.editingDocument = new Document(model);
    this.editDocumentModalOpen = true;
  }

  closeEditDocumentModal() {
    this.editingDocument = {};
    this.editDocumentModalOpen = false;
  }

  async createDocument(file, folder, shareWithEmployee = false) {
    const doc = new Document({
      key: file.key,
      fileName: file.fileName,
      fileSize: file.fileSize,
      fileType: 'Employee File',
      contentType: file.contentType || 'application/octet-stream',
      shareWithEmployee: shareWithEmployee,
      folder: folder
    });

    const {model, errors} = await this.store.post(
      endpoints.EMPLOYEE_DOCUMENTS.with(this.employeeId),
      types.DOCUMENT,
      doc
    );

    if (model) {
      this.refreshTable();
    } else {
      errorAlert(
        t('components.upload.FAILED_TO_UPLOAD', { file: file.name, message: errors.contentType })
      );
    }
  }

  async updateDocument() {
    const { model, errors } = await this.store.patch(this.editingDocument);
    this.errors = errors;

    if (model) {
      successAlert(t('employees.profile.documents.alerts.Successfully updated document'));
      this.refreshTable();
      this.closeEditDocumentModal();
    }
  }

  async deleteDocument(model) {
    await this.store.destroy(model);
    this.refreshTable();
  }

  openMoveDocumentModal(model) {
    this.movingDocument = model;
    this.movingDocument.folder = null;
    this.moveDocumentModalOpen = true;
  }

  closeMoveDocumentModal() {
    this.moveDocumentModalOpen = false;
  }

  async moveDocumentToFolder() {
    const {model} = await this.store.patch(this.movingDocument);

    if (model) {
      successAlert(t('employees.profile.documents.alerts.Successfully moved document'));
      this.refreshTable();
      this.closeMoveDocumentModal();
    }
  }

  closeAssignDocumentModal() {
    this.assignDocumentModalOpen = false;
    setTimeout(() => {
      this.assigningDocument = {};
      this.errors = {};
    }, 500);
  }

  openAnnotatorModal() {
    this.assignDocumentModalOpen = false;
    if (_.isEmpty(this.assigningDocument.assignmentRules)) {
      this.assigningDocument.addRule({assigneeType: 'employee'});
    }
    this.annotatorModalOpen = true;
  }

  closeAnnotatorModal() {
    this.annotatorModalOpen = false;
    setTimeout(() => {
      this.assigningDocument = {};
      this.errors = {};
    }, 500);
  }

  selectDocumentType(type) {
    this.assigningDocument.merge({ documentType: type });
    this.acceptedMimeTypes = type === 'pdf_template' ? 'application/pdf' : '';
  }

  selectDocumentAction(action) {
    this.documentAction = action;
  }

  async saveUploadedFile() {
    if (!this.validateCompanyDocument()) return;
    if (this.uploadingPDFTemplate) {
      this.openAnnotatorModal();
    } else {
      await this.assignDocument();
    }
  }

  async assignExistingDocument() {
    const {model, errors} = await this.store.post(
      endpoints.DOCUMENT_ACTIONS.with(this.companyDocumentAssignment.companyDocument.id),
      types.COMPANY_DOCUMENT_ASSIGNMENT,
      this.companyDocumentAssignment
    );
    this.errors = errors;

    if (model) {
      this.closeAssignDocumentModal();
      successAlert(t('employees.profile.documents.alerts.Successfully assigned document'));
    }
  }

  async addAnnotations(annotations) {
    this.assigningDocument.annotations = annotations;
    if (!this.validateCompanyDocument()) return;
    await this.assignDocument();
  }

  async assignDocument() {
    const {model, errors} = await this.store.put(
      endpoints.ASSIGN_COMPANY_DOCUMENT.with(this.employeeId),
      types.COMPANY_DOCUMENT,
      new CompanyDocument(this.assigningDocument)
    );

    this.errors = errors;

    if (model) {
      this.closeAnnotatorModal();
      this.closeAssignDocumentModal();
      successAlert(t('employees.profile.documents.alerts.Successfully assigned document'));
    }
  }

  refreshTable() {
    if (this.interactiveAgent) {
      this.interactiveAgent.refresh();
      this.interactiveAgent.refreshFolders();
    }
  }

  documentCustomLinks(model) {
    if (_.isEmpty(this.allFolders)) return [];
    if (!model.hasLink('self')) return [];

    return [{
      order: 1,
      text: 'employees.profile.documents.Move to Folder...',
      action: () => this.openMoveDocumentModal(model),
    }];
  }

  get customFolderLinks() {
    const {parentState} = this;
    if (!parentState.editDataPermission('::DOCUMENTS')) return null;

    return [
      {
        order: 1,
        text: 'employees.profile.documents.Upload File(s)',
        action: folder => this.openUploadModal(folder),
      },
      {
        order: 2,
        text: 'employees.profile.documents.Assign Document',
        action: folder => this.openAssignDocumentModal(folder),
      }
    ];
  }

  get choosingDocumentAction() {
    return !this.uploadingNewDocument && !this.assigningExistingDocument;
  }

  get choosingDocumentType() {
    return !this.uploadingPDFTemplate && !this.uploadingStaticDocument;
  }

  get uploadingNewDocument() {
    return this.documentAction === 'new';
  }

  get assigningExistingDocument() {
    return this.documentAction === 'existing';
  }

  get uploadingPDFTemplate() {
    return this.assigningDocument.documentType === 'pdf_template';
  }

  get uploadingStaticDocument() {
    return this.assigningDocument.documentType === 'static_document';
  }

  get employeeId() {
    return this.parentState.match.params.id;
  }

  get canShareWithEmployee() {
    return auth.employee.id !== this.employeeId && this.parentState.editDataPermission('::DOCUMENTS');
  }

  get foldersInModalDropdown() {
    return this.allFolders;
  }

  validateCompanyDocument() {
    this.errors = {};

    if (!this.assigningDocument.name) {
      this.errors.name = t('employees.profile.documents.errors.Name is required.');
    }

    if (_.isEmpty(this.assigningDocument.originalFile)) {
      this.errors.awsFiles = t('employees.profile.documents.errors.A file must be uploaded.');
    }

    return _.isEmpty(this.errors);
  }
}

export default EmployeeDocumentState;
